import { SidePanel, sidePanelTransitionDuration } from "$root/components/common/SidePanelV2";
import { StackOutlet, type StackRouterProps, useStackRouter } from "$root/components/common/Stack";
import type { SetterOrUpdater } from "@mdotm/shadcn/legacy/utils";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { createContext, type ReactNode, useCallback, useMemo, useState } from "react";

export type StackSidePanelContextProps = {
	setOpen: SetterOrUpdater<boolean>;
} & StackRouterProps;

/**
 * The panel's actions — `push` / `pop` / `reset` / `setOpen` and the stack atom. One identity for the
 * provider's lifetime, so subscribing to it costs a consumer nothing: this is what the workspace grid,
 * the workspace charts and the lab charts read, and they must not re-render when the panel opens.
 *
 * Whether the panel is open is {@link StackSidePanelOpenContext}, deliberately apart from these. The
 * provider's children are the whole Workspace / Portfolio Lab page, so a value that changed on open
 * would pull that entire page back into the render React would otherwise skip.
 */
export const StackSidePanelContext = createContext<StackSidePanelContextProps | null>(null);

/** Whether the panel is open (see {@link StackSidePanelContext} for why it is its own context). */
export const StackSidePanelOpenContext = createContext<boolean | null>(null);

export function StackSidePanelContextProvider(props: { children: ReactNode; root: React.ComponentType }) {
	const [open, setOpen] = useState(false);
	const stackRouterCtx = useStackRouter({ root: props.root });
	const pushRefs = useUnsafeUpdatedRef({ open, stackRouterCtx });

	const push = useCallback(
		(params: Parameters<StackRouterProps["push"]>[0]) => {
			if (!pushRefs.current.open) {
				setOpen(true);
				pushRefs.current.stackRouterCtx.push(params, { skipTransition: true });
			} else {
				pushRefs.current.stackRouterCtx.push(params);
			}
		},
		[pushRefs],
	);

	const actions = useMemo<StackSidePanelContextProps>(
		() => ({ ...stackRouterCtx, push, setOpen }),
		[stackRouterCtx, push],
	);

	return (
		<StackSidePanelContext value={actions}>
			<StackSidePanelOpenContext value={open}>{props.children}</StackSidePanelOpenContext>
			<SidePanel
				mode="overlay"
				position="right"
				open={open}
				onClose={() => {
					setOpen(false);
					setTimeout(() => {
						stackRouterCtx.reset();
					}, sidePanelTransitionDuration);
				}}
				onAnimationStateChange={(state) => state === "exited" && setOpen(false)}
			>
				<StackOutlet {...stackRouterCtx} />
			</SidePanel>
		</StackSidePanelContext>
	);
}
